import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface VendorFilterDropdownProps {
  vendors: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Number of vendors currently displayed in the filtered results */
  displayedCount?: number;
}

export function VendorFilterDropdown({
  vendors,
  selected,
  onChange,
  displayedCount,
}: VendorFilterDropdownProps) {
  const [open, setOpen] = useState(false);

  const toggle = (vendor: string) => {
    const next = new Set(selected);
    if (next.has(vendor)) {
      next.delete(vendor);
    } else {
      next.add(vendor);
    }
    onChange(next);
  };

  const selectAll = () => onChange(new Set(vendors));
  const clearAll = () => onChange(new Set());

  const countSuffix = displayedCount != null ? ` · ${displayedCount} shown` : "";

  let triggerLabel: string;
  if (selected.size === 0) {
    triggerLabel = `All vendors${countSuffix}`;
  } else if (selected.size === 1) {
    triggerLabel = `${[...selected][0]}${countSuffix}`;
  } else {
    triggerLabel = `${selected.size} vendors selected${countSuffix}`;
  }

  const hasSelection = selected.size > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-8 px-3 text-xs font-medium justify-between gap-2 max-w-[280px]",
            hasSelection && "border-primary/50 bg-primary/5 text-primary"
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <div className="flex items-center gap-1 shrink-0">
            {hasSelection && (
              <span
                role="button"
                aria-label="Clear vendor filter"
                className="rounded-sm hover:bg-primary/20 p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  clearAll();
                }}
              >
                <X className="w-3 h-3" />
              </span>
            )}
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search vendors…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty className="py-4 text-xs text-center text-muted-foreground">
              No vendors found.
            </CommandEmpty>

            {/* Bulk actions */}
            <CommandGroup>
              <CommandItem
                onSelect={selectAll}
                className="text-xs text-muted-foreground cursor-pointer"
              >
                <Check
                  className={cn(
                    "mr-2 w-3.5 h-3.5",
                    selected.size === vendors.length ? "opacity-100" : "opacity-0"
                  )}
                />
                Select all ({vendors.length})
              </CommandItem>
              {hasSelection && (
                <CommandItem
                  onSelect={clearAll}
                  className="text-xs text-muted-foreground cursor-pointer"
                >
                  <X className="mr-2 w-3.5 h-3.5 opacity-60" />
                  Clear selection
                </CommandItem>
              )}
            </CommandGroup>

            <CommandSeparator />

            {/* Vendor list */}
            <CommandGroup>
              {vendors.map((vendor) => {
                const isSelected = selected.has(vendor);
                return (
                  <CommandItem
                    key={vendor}
                    value={vendor}
                    onSelect={() => toggle(vendor)}
                    className="text-xs cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 w-3.5 h-3.5 text-primary shrink-0",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{vendor}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
